import { useRef, useState, type DragEvent } from 'react';
import { Icone } from './Icone';

interface ProprietesDepot {
  /** Extensions proposées au sélecteur du système, ex. `.csv,.txt`. */
  accept?: string;
  /** Nom du fichier retenu, `null` tant que rien n'a été chargé. */
  nomFichier: string | null;
  /** Reçoit le contenu décodé en UTF-8 et le nom d'origine du fichier. */
  onFichier: (contenu: string, nom: string) => void;
  /** Rendu du bouton « Retirer ». Absent ⇒ pas de retrait possible. */
  onRetirer?: () => void;
  /** Ligne d'aide sous la zone : formats admis, conventions du fichier. */
  aide?: string;
  desactive?: boolean;
}

/** Un fichier de situation ou de cours tient en quelques centaines de kio. */
const TAILLE_MAX = 8 * 1024 * 1024;

/**
 * Zone de dépôt d'un fichier **texte**, lu sur le poste et rendu au parent.
 *
 * Elle ne remplace pas le collage : les deux écrans de réception gardent leur
 * zone de saisie, qu'un fichier chargé vient simplement remplir. Le middle
 * office reçoit le fichier du tiers par messagerie ; l'ouvrir dans un éditeur
 * pour en recopier le contenu était une manipulation de trop, et une occasion
 * de tronquer.
 *
 * Le BOM éventuel est retiré à la lecture : un même contenu doit produire la
 * même empreinte, qu'il ait été collé ou déposé — sans quoi l'idempotence de
 * l'import (C-08) ne tiendrait plus.
 */
export function DepotFichierTexte({
  accept = '.csv,.txt',
  nomFichier,
  onFichier,
  onRetirer,
  aide,
  desactive,
}: ProprietesDepot) {
  const entree = useRef<HTMLInputElement>(null);
  const [survol, setSurvol] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  async function lire(fichier: File | undefined) {
    if (!fichier) return;
    setErreur(null);
    if (fichier.size > TAILLE_MAX) {
      setErreur(
        `Fichier trop volumineux (${Math.round(fichier.size / 1024)} kio). Limite : ${TAILLE_MAX / 1024 / 1024} Mio.`,
      );
      return;
    }
    try {
      const contenu = (await fichier.text()).replace(/^﻿/, '');
      if (!contenu.trim()) {
        setErreur('Le fichier est vide.');
        return;
      }
      onFichier(contenu, fichier.name);
    } catch {
      setErreur('Fichier illisible.');
    }
  }

  function deposer(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setSurvol(false);
    if (desactive) return;
    void lire(e.dataTransfer.files[0]);
  }

  return (
    <div className="champ">
      <div
        className={`depot${survol ? ' depot--survol' : ''}${nomFichier ? ' depot--rempli' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!desactive) setSurvol(true);
        }}
        onDragLeave={() => setSurvol(false)}
        onDrop={deposer}
      >
        <Icone nom={nomFichier ? 'piece' : 'import'} taille={20} classe="depot__icone" />

        {nomFichier ? (
          <span className="depot__nom">{nomFichier}</span>
        ) : (
          <span className="depot__invite">Glissez le fichier ici, ou</span>
        )}

        <button
          type="button"
          className="bouton bouton--fantome bouton--compact"
          disabled={desactive}
          onClick={() => entree.current?.click()}
        >
          {nomFichier ? 'Remplacer' : 'Choisir un fichier'}
        </button>

        {nomFichier && onRetirer && (
          <button
            type="button"
            className="bouton bouton--fantome bouton--compact"
            disabled={desactive}
            onClick={() => {
              setErreur(null);
              onRetirer();
            }}
          >
            Retirer
          </button>
        )}

        <input
          ref={entree}
          className="depot__entree"
          type="file"
          accept={accept}
          disabled={desactive}
          onChange={(e) => {
            void lire(e.target.files?.[0]);
            // Remis à zéro : recharger deux fois le même fichier corrigé
            // n'émettrait aucun événement sans cela.
            e.target.value = '';
          }}
        />
      </div>

      {erreur ? (
        <span className="champ__message">{erreur}</span>
      ) : aide ? (
        <span className="champ__aide">{aide}</span>
      ) : null}
    </div>
  );
}
